import { ColumnSetting, TableColumnEnum } from 'components/Table/utils';
import { useCallback, useMemo } from 'react';
import { CaseEditOperation } from './useQueuedCaseUpdates';
import { AssignmentViewModel, BasicCaseModel, CompanyModel, TranslationLookups } from 'api/model';
import {
  makeDateComparator,
  makeNumberComparator,
  makeStringComparator,
} from 'utils/sorting';
import useTranslations from 'contexts/basicData/useTranslations';
import { formatDate, getDaysBetween } from 'utils/date-helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import TextButton from 'components/inputs/TextButton';

export interface ColumnProps {
  onSelectClick(assignment: AssignmentViewModel): void;
  selectedAssignmentIds: Set<number>;
  translations: TranslationLookups;
  onUpdateAssignment(
    assignmentId: number,
    editOperation: CaseEditOperation
  ): void;
  companies: { [lookupString: string]: CompanyModel };
}

const useUnclosedCasesListColumnSettings = (): ColumnSetting<
  BasicCaseModel,
  ColumnProps
>[] => {
  const { caseStatuses, caseTypes } = useTranslations();

  const goToCase = useCallback((caseId: number) => {
    window.open(
      `/arende/${caseId}`,
      `/arende/${caseId}`,
      'noopener,noreferrer'
    );
  }, []);

  return useMemo(() => {
    type Column = ColumnSetting<BasicCaseModel, ColumnProps>;
    const columns: Column[] = [
      {
        identifier: TableColumnEnum.RowNumber,
        head: 'Nr',
        csvHead: 'Nr',
        render: (model, props, focused, rowIndex) => `${rowIndex + 1}`,
        width: 50,
      },
      {
        identifier: TableColumnEnum.CaseAge,
        head: 'Ålder (dagar)',
        csvHead: 'Ålder (dagar)',
        render: (model) => getDaysBetween(model.created, new Date()),
        width: 120,
        sortFunction: makeNumberComparator((m) =>
          getDaysBetween(m.created, new Date())
        ),
      },
      {
        identifier: TableColumnEnum.Created,
        head: 'Skapat',
        csvHead: 'Skapat',
        render: (model) => formatDate(model.created),
        width: 150,
        sortFunction: makeDateComparator((m) => m.created),
      },
      {
        identifier: TableColumnEnum.CaseID,
        head: 'ID',
        csvHead: 'ID',
        render: (model) => model.caseID,
        width: 100,
        sortFunction: makeNumberComparator((m) => m.caseID),
      },
      {
        identifier: TableColumnEnum.CaseType,
        head: 'Typ',
        csvHead: 'Typ',
        render: (model) => caseTypes[model.caseTypeID],
        width: 150,
        sortFunction: makeStringComparator(
          (m) => caseTypes[m.caseTypeID] ?? ''
        ),
      },
      {
        identifier: TableColumnEnum.CaseRegistrationNumber,
        head: 'Regnr',
        csvHead: 'Regnr',
        render: (model) => model.registrationNumber,
        width: 100,
        sortFunction: makeStringComparator((m) => m.registrationNumber),
      },
      {
        identifier: TableColumnEnum.CaseStatus,
        head: 'Status',
        csvHead: 'Status',
        render: (model) => caseStatuses[model.caseStatusID],
        width: 150,
      },
      {
        identifier: TableColumnEnum.AssignmentsCount,
        head: 'Antal uppdrag',
        csvHead: 'Antal uppdrag',
        render: (model) => model.assignmentsCount,
        width: 150,
        sortFunction: makeNumberComparator((m) => m.assignmentsCount),
      },
      {
        head: 'Länk',
        csvHead: 'Länk',
        render: (model) => (
          <TextButton onClick={() => goToCase(model.caseID)}>
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </TextButton>
        ),
        width: 50,
      },
    ] as Column[];

    return columns.filter((c) => !!c); // remove undefined before returning columns
  }, [caseStatuses, caseTypes, goToCase]);
};

export default useUnclosedCasesListColumnSettings;
